import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { SocketService } from './socket.service';
import { Observable, BehaviorSubject, firstValueFrom } from 'rxjs';
import { Role } from 'src/app/shared/models/role.model';
/**
 * Service for role
 */
@Injectable({ providedIn: 'root' })
/**
 * Service for role
 * @class RoleService
 */
export class RoleService {
  /**
   * Creates an instance of RoleService.
   * @param {HttpClient} http
   */
  private http = inject(HttpClient);
  /**
   * Creates an instance of socketService.
   * @param {SocketService} socketService
   */
  private socketService = inject(SocketService);

  /**
   * Api url
   * @type {string}
   */
  private baseApiUrl = environment.apiUrl + 'company/role';
  /**
   * Echo instance
   * @type {any}
   */
  private echoInstance = null;
  /**
   * Socket for role
   * @type {BehaviorSubject<Role[]>}
   */
  private socketRole$: BehaviorSubject<Role[]> = new BehaviorSubject([]);

  /**
   * Start the socket for the role service
   * Gets all roles and listen to RoleUpdatedEvent, RoleAddedEvent, RoleDeletedEvent
   * @returns {Promise<void>}
   */
  startSocket() {
    this.getAllRoles()
      .then((suc) => {
        if (suc.success === 1) {
          this.socketRole$.next(suc.roles);
        }
      })
      .catch(() => { })
      .finally(() => {
        this.echoInstance = this.socketService.getEchoInstance();
        this.echoInstance.private(`company.${this.socketService.companyId}.role`)
          .listen('.RoleUpdatedEvent', () => {
            this.updateLocalRoles();
          })
          .listen('.RoleAddedEvent', () => {
            this.updateLocalRoles();
          })
          .listen('.RoleDeletedEvent', () => {
            this.updateLocalRoles();
          });
      });
  }

  /**
   * Update the local roles
   * Fetches all roles and updates the socketRole$ observable with the new roles.
   * @returns {Promise<void>}
   */
  private updateLocalRoles() {
    this.getAllRoles()
      .then((suc) => {
        if (suc.success === 1) {
          this.socketRole$.next(suc.roles);
        }
      })
      .catch(() => { });
  }

  /**
   * Gets all roles
   * @returns {Promise<any>} 
   */
  getAllRoles(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/all'));
  }

  /**
   * Retrieves roles based on the provided filters.
   * Sends a GET request to the API with the specified filters as query parameters.
   * @param filters The filters to apply when retrieving roles.
   * @returns {Promise<any>} A promise of the response containing the filtered roles.
   */
  getRoles(filters): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl, { params: filters }));
  }

  /**
   * Adds a new role.
   * Sends a POST request to the API with the role data.
   * @param data The data of the role to be added.
   * @returns {Promise<any>}
   */
  addRole(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl, data));
  }

  /**
   * Updates an existing role.
   * Sends a PUT request to the API with the updated role data.
   * @param data The updated data of the role to be modified.
   * @returns {Promise<any>}
   */
  updateRole(data): Promise<any> {
    return firstValueFrom(this.http.put(this.baseApiUrl, data));
  }

  /**
   * Deletes a role.
   * Sends a DELETE request to the API with the role data.
   * @param data The data of the role to be deleted.
   * @returns {Promise<any>}
   */
  deleteRole(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', this.baseApiUrl, { body: data }));
  }

  /**
   * Stop the socket for the role service.
   * Leaves the private channel for the company's roles and clears the local state of the roles.
   * @returns {void}
   */
  stopSocket() {
    if (this.echoInstance) {
      this.echoInstance.leave(`company.${this.socketService.companyId}.role`);
      this.socketRole$.next([]);
    }
  }

  /**
   * Returns an observable that emits values when a role is added, updated, or deleted.
   * The values are arrays of roles, each role being an object with the role's properties.
   * @returns {Observable<any>}
   */
  subSocketRole(): Observable<any> {
    return this.socketRole$;
  }
}

export type ToggleKey =
  | 'manageGrc'
  | 'accessConsole'
  | 'manageUsers'
  | 'accessDashboard'
  | 'managePreloadPasswords'
  | 'manageCreditCards'
  | 'manageApplicationAccess'
  | 'seePasswords'
  | 'seeCreditCards'
  | 'seePreloadedUserAccess'
  | 'copyCVV'
  | 'viewCVV'
  | 'seeCardNumber'
  | 'copyAccessAKPassword'
  | 'makeAccessAKPasswordVisible'
  | 'scanTwoFAAccessKit'
  | 'copyPassword'
  | 'makePasswordVisible'
  | 'createLoginForPreloadUrl'
  | 'createPasswordForPreloadUrl'
  | 'createPassword'
  | 'manageDeviceRestriction'
  | 'applyDeviceRestriction'
  | 'clearInputFieldsByExtension'
  | 'manageDisableAutoFillExtension'
  | 'manageSetting'
  | 'reportsUserInactivity'
  | 'forceTwoFa'
  | 'loginWithoutTFA';
